'use client';

import path from 'path';
import * as React from 'react';
import { useMemo } from 'react';
import SwaggerParser from '@apidevtools/swagger-parser';
import yaml from 'js-yaml';
import { Box } from '@unity/cloud-ui';
import { SpinnerIcon } from '@phosphor-icons/react';
import { getServerEnv } from '@/lib/constants/environment';
import { transformRelativeUrl } from '@/lib/content-loader/remark-plugins/utils';
import { OpenAPI } from './openapi';
import { OpenAPIPublic } from './openapi-public';

interface OpenAPIRendererProps {
  specPath: string;
  currentFilePath?: string;
  isIndex?: boolean;
}

type OpenAPIDoc = Awaited<ReturnType<typeof SwaggerParser.dereference>>;

export const OpenAPIRenderer = (props: OpenAPIRendererProps) => {
  const { specPath, currentFilePath, isIndex } = props;
  const [processedSpec, setProcessedSpec] = React.useState<OpenAPIDoc | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  // Important: it should never be "true" in production
  const localDevelopmentFlag = getServerEnv().SHOW_UNRELEASED_CONTENT;

  // Transform specPath to the appropriate URL if needed
  const transformedSpecPath = useMemo(() => {
    if (!specPath) {
      return '';
    }

    if (specPath.startsWith('http://') || specPath.startsWith('https://')) {
      return specPath;
    }

    // Transform absolute paths to API route
    if (specPath.startsWith('/')) {
      return `/api/openapi?file=${encodeURIComponent(specPath)}`;
    }

    // Transform relative paths to API route
    if (specPath.startsWith('.') && currentFilePath) {
      const currentDirectory = isIndex ? currentFilePath : path.dirname(currentFilePath);
      const transformedPath = transformRelativeUrl(specPath, currentDirectory);
      return `/api/openapi?file=${encodeURIComponent(transformedPath)}`;
    }

    return specPath;
  }, [specPath, currentFilePath, isIndex]);

  React.useEffect(() => {
    if (!localDevelopmentFlag) {
      setIsLoading(false);
      return;
    }

    const fetchAndProcessSpec = async () => {
      if (!transformedSpecPath) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch the spec from the API route
        const response = await fetch(transformedSpecPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch spec: ${response.statusText}`);
        }

        const specText = await response.text();

        // Parse YAML to JSON
        const specJson = yaml.load(specText);

        // Process with swagger-parser to dereference $ref pointers
        const api = await SwaggerParser.dereference(specJson);

        setProcessedSpec(api);
      } catch (error) {
        console.error('[OpenAPIRenderer] Error fetching/processing spec:', error);
        setProcessedSpec(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessSpec();
  }, [localDevelopmentFlag, transformedSpecPath]);

  if (isLoading) {
    return (
      <Box
        sx={{ p: 3, width: '100%', height: '100%' }}
        display={'flex'}
        alignItems="center"
        justifyContent={'center'}
        gap={2}
      >
        <Box
          sx={{
            display: 'inline-flex',
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              from: {
                transform: 'rotate(0deg)',
              },
              to: {
                transform: 'rotate(360deg)',
              },
            },
          }}
        >
          <SpinnerIcon size={48} />
        </Box>
      </Box>
    );
  }

  if (processedSpec && localDevelopmentFlag) {
    return <OpenAPIPublic spec={processedSpec} />;
  }

  return <OpenAPI specPath={transformedSpecPath} />;
};
